// Компонент для подключения к Wildberries через API-токен (без аккаунта — токен и есть ключ доступа)
import { useState } from 'react'
import { useWildberries } from '../../hooks/useWildberries'
import { useWbToken } from '../../hooks/useWbToken'
import { Eye, EyeOff, HelpCircle, CheckCircle, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'

interface WBConnectionProps {
  onConnected?: () => void
  showInstructions?: boolean
}

export const WBConnection: React.FC<WBConnectionProps> = ({
  onConnected,
  showInstructions = true
}) => {
  const [token, setToken] = useState('')
  const [showToken, setShowToken] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const { isConnected, connecting, connect, disconnect } = useWbToken()
  const { error, verifyConnection } = useWildberries()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token.trim()) {
      toast.error('Введите API-токен')
      return
    }

    const success = await connect(token)
    if (success) {
      toast.success('✅ Токен Wildberries успешно подключён')
      setToken('')
      if (onConnected) onConnected()
    }
  }

  const handleVerify = async () => {
    const connected = await verifyConnection()
    if (connected) {
      toast.success('Подключение к Wildberries активно')
    } else {
      toast.error('Подключение отсутствует или токен недействителен')
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Подключение Wildberries</h2>
          <p className="text-gray-600 mt-1">
            Вставьте API-токен продавца — регистрация не требуется, проверки начинаются сразу
          </p>
        </div>
        {isConnected && (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle size={24} />
            <span className="font-medium">Подключён</span>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="text-red-500 mt-0.5" size={20} />
          <div>
            <p className="text-red-800 font-medium">Ошибка подключения</p>
            <p className="text-red-700 text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      {!isConnected ? (
        <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
          <div>
            <label htmlFor="wb-token" className="block text-sm font-medium text-gray-700 mb-2">
              API-токен Wildberries
            </label>
            <div className="relative">
              <input
                id="wb-token"
                type={showToken ? 'text' : 'password'}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Введите ваш API-токен"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                name="wb-token-unique-id"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 pr-12"
                disabled={connecting}
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                disabled={connecting}
              >
                {showToken ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Сам токен на сервере не хранится — только его хэш для привязки истории проверок
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={connecting || !token.trim()}
              className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {connecting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Подключение...
                </>
              ) : (
                'Подключить'
              )}
            </button>

            {showInstructions && (
              <button
                type="button"
                onClick={() => setShowHelp(true)}
                className="px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 flex items-center gap-2"
              >
                <HelpCircle size={18} />
                Где взять токен?
              </button>
            )}
          </div>
        </form>
      ) : (
        <div className="space-y-6">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-3">
              <CheckCircle className="text-green-600" size={24} />
              <div>
                <p className="font-medium text-green-800">Токен Wildberries подключён</p>
                <p className="text-green-700 text-sm mt-1">
                  Теперь вы можете загружать и проверять карточки товаров
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => {
                if (onConnected) onConnected()
              }}
              className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Загрузить карточки
            </button>
            <button
              onClick={() => {
                disconnect()
                setToken('')
              }}
              className="px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Сменить токен
            </button>
            <button
              onClick={handleVerify}
              className="px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Проверить подключение
            </button>
          </div>
        </div>
      )}

      {/* Модалка с инструкцией */}
      {showHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Где взять API-токен Wildberries?</h3>
                <button
                  onClick={() => setShowHelp(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg">
                  <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Зайдите в личный кабинет селлера на Wildberries</h4>
                    <p className="text-gray-700 text-sm mt-1">
                      Перейдите на{' '}
                      <a
                        href="https://seller.wildberries.ru"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:underline"
                      >
                        seller.wildberries.ru
                      </a>{' '}
                      и авторизуйтесь
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg">
                  <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Перейдите в Настройки → Доступ к API</h4>
                    <p className="text-gray-700 text-sm mt-1">
                      В боковом меню выберите "Настройки", затем "Доступ к API"
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg">
                  <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Создайте новый токен с правами "Чтение товаров"</h4>
                    <p className="text-gray-700 text-sm mt-1">
                      Нажмите "Создать токен", выберите разрешение "Товары (чтение)" и сохраните
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg">
                  <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Скопируйте и вставьте токен в форму</h4>
                    <p className="text-gray-700 text-sm mt-1">
                      Скопируйте сгенерированный токен (длинная строка символов) и вставьте в поле выше
                    </p>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="font-medium text-yellow-800 mb-2">⚠️ Важно!</h4>
                  <ul className="text-yellow-700 text-sm space-y-1">
                    <li>• Никому не передавайте ваш токен — это конфиденциальные данные</li>
                    <li>• Токен хранится только в вашем браузере, на сервере — лишь его хэш</li>
                    <li>• При утере токена вы можете отозвать его в личном кабинете WB</li>
                    <li>• Для работы нужны только права на чтение товаров</li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowHelp(false)}
                  className="px-6 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700"
                >
                  Понятно
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}